import TMembershipTemplateRepository from '@/domains/membershipTemplates/api/TMembershipTemplateRepository'

const getDefaultState = () => ({
  list: null,
  loading: false
})

export default {
  namespaced: true,
  state: getDefaultState(),

  mutations: {
    resetState(state) {
      Object.assign(state, getDefaultState())
    },
    setLoading(state, loading) {
      state.loading = loading
    },
    setList(state, list) {
      state.list = Array.isArray(list) ? list : []
    },
    addOne(state, template) {
      if (!template || template.r_membership_template_id == null) 
        return

      if (!state.list)
        state.list = []

      const idx = state.list.findIndex(t => t.r_membership_template_id === template.r_membership_template_id)
      if (idx >= 0) {
        state.list.splice(idx, 1, template)
      } else {
        state.list.push(template)
      }
    },
    replaceOne(state, template) {
      if (!template || template.r_membership_template_id == null)
        return

      const idx = state.list.findIndex(t => t.r_membership_template_id === template.r_membership_template_id)
      if (idx >= 0) {
        state.list.splice(idx, 1, template)
      } else {
        state.list.push(template)
      }
    },
    removeOne(state, rMembershipTemplateId) {
      const idx = state.list.findIndex(t => t.r_membership_template_id === rMembershipTemplateId)
      if (idx >= 0) {
        state.list.splice(idx, 1)
      }
    }
  },

  actions: {
    resetState({ commit }) {
      commit('resetState')
    },

    /**
     * Load membership templates: fetch only when not already loaded, unless force is true.
     * Use force: true when opening the membership template view (where templates are edited).
     * Use force: false (default) in dialogs so they reuse cached list.
     * @param {{ force?: boolean }} payload - { force: true } to always refetch
     * @returns {Promise<Array>} List of membership templates
     */
    async loadMembershipTemplates({ state, commit }, { force = false } = {}) {
      if (!force && state.list != null)
        return state.list

      commit('setLoading', true)
      try {
        const list = await TMembershipTemplateRepository.getAllMembershipTemplates()
        commit('setList', list)
      } finally {
        commit('setLoading', false)
      }
    },

    /**
     * Create a new membership template. Updates store with the created item (no refetch).
     */
    async createMembershipTemplate({ commit }, payload) {
      const created = await TMembershipTemplateRepository.createMembershipTemplate(payload)
      commit('addOne', created)
      return created
    },

    /**
     * Update an existing membership template. Updates store with the response (no refetch).
     */
    async updateMembershipTemplate({ commit }, { id, data }) {
      const updated = await TMembershipTemplateRepository.updateMembershipTemplate(id, data)
      commit('replaceOne', updated)
      return updated
    },

    /**
     * Delete a membership template. Removes it from store (no refetch).
     * @param {number} rMembershipTemplateId - r_membership_template_id from API
     */
    async deleteMembershipTemplate({ commit }, rMembershipTemplateId) {
      await TMembershipTemplateRepository.deleteMembershipTemplate(rMembershipTemplateId)
      commit('removeOne', rMembershipTemplateId)
    }
  },

  getters: {
    /** All membership templates (for table / list). */
    membershipTemplates: state => state.list,
    /** Loading flag for fetch. */
    loading: state => state.loading,
    /** Get a single template by id (r_membership_template_id). Use for edit form. */
    getMembershipTemplateById: state => id => {
      if (id == null) return null
      const numId = typeof id === 'number' ? id : parseInt(id, 10)
      if (Number.isNaN(numId)) return null
      return state.list?.find(t => t.r_membership_template_id === numId) || null
    }
  }
}
